import React, { useState } from 'react';
import { checkout } from '../services/api';
import { CheckoutRequest } from '../types/api';

interface CheckoutProps {
  productName: string;
  price: number;
}

type PaymentMethod = 'payid' | 'bank' | null;

export const Checkout: React.FC<CheckoutProps> = ({ productName, price }) => {
  const [formData, setFormData] = useState<CheckoutRequest>({
    name: '',
    email: '',
    quantity: 1,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkoutData, setCheckoutData] = useState<any>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await checkout(formData);
      setCheckoutData(response.data);
    } catch (error) {
      alert('There was an error processing your purchase. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'quantity' ? parseInt(value) : value,
    }));
  };

  return (
    <form onSubmit={handleSubmit}>
      {checkoutData && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="space-y-6">
            {/* Success Message */}
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-green-600 mb-2">Your Order is Successfully Submitted</h2>
            </div>

            {/* Order Details */}
            <div>
              <h3 className="text-xl font-medium text-gray-900 mb-4">Order Details</h3>
              <div className="space-y-2">
                <p className="text-base text-gray-900">Name: {checkoutData.name}</p>
                <p className="text-base text-gray-900">E-mail: {checkoutData.email}</p>
                <p className="text-base text-gray-900">Quantity: {checkoutData.quantity} Pass{checkoutData.quantity > 1 ? 'es' : ''}</p>
              </div>
            </div>

            {/* Payment Details */}
            <div>
              <h3 className="text-xl font-medium text-green-600 mb-4">Almost done! Please complete your payment to receive your voucher.</h3>
              {/*<p className="text-base text-gray-600 mb-4">Please select your preferred payment method:</p>*/}
              <h3 className="text-xl font-medium text-gray-900 mt-8 mb-4">Step 1: Transfer ${checkoutData.amount} via Bank Transfer or PayID</h3>
              <div className="grid grid-cols-1 gap-4">
                <button
                  type="button"
                  onClick={() => setSelectedPaymentMethod(selectedPaymentMethod === 'payid' ? null : 'payid')}
                  className={`p-4 rounded-lg border-2 text-left transition-all duration-300 ${
                    selectedPaymentMethod === 'payid'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div
                      className="flex items-center"
                      style={{
                        backgroundImage: "url('/images/pay-id.svg')",
                        backgroundSize: '60px',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'left center',
                      }}
                  >
                    {/*<img src="/images/pay-id.svg" alt="PayID Logo" className="h-12 mr-3" />*/}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 ml-16">PayID</h4>
                      {/*<p className="text-sm text-gray-600">Fast and secure payment</p>*/}
                    </div>
                  </div>
                  {selectedPaymentMethod === 'payid' && (
                    <div className="mt-4 animate-fadeIn">
                      <div className="bg-white p-4 rounded-md space-y-4">
                        <div>
                          <p className="text-sm text-gray-500">PayID</p>
                          <p className="text-lg font-medium text-gray-900 mt-1">34668403478</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Reference</p>
                          <p className="text-lg font-medium text-gray-900 mt-1">{checkoutData.referenceCode}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Amount</p>
                          <p className="text-lg font-medium text-gray-900 mt-1">${checkoutData.amount}</p>
                        </div>
                      </div>
                      <div className="mt-4 p-4 bg-yellow-50 rounded-md">
                        <p className="text-base font-medium">⚠️ Payments without the correct Reference may not be processed.</p>
                      </div>
                    </div>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedPaymentMethod(selectedPaymentMethod === 'bank' ? null : 'bank')}
                  className={`p-4 rounded-lg border-2 text-left transition-all duration-300 ${
                    selectedPaymentMethod === 'bank'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">Bank Transfer</h4>
                    {/*<p className="text-sm text-gray-600">Traditional bank transfer</p>*/}
                  </div>
                  {selectedPaymentMethod === 'bank' && (
                    <div className="mt-4 animate-fadeIn">
                      <div className="bg-white p-4 rounded-md space-y-4">
                        <div>
                          <p className="text-sm text-gray-500">Account Name</p>
                          <p className="text-lg font-medium text-gray-900 mt-1">APPVANZ PTY LTD</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">BSB</p>
                          <p className="text-lg font-medium text-gray-900 mt-1">067-873</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Account Number</p>
                          <p className="text-lg font-medium text-gray-900 mt-1">17389140</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Description</p>
                          <p className="text-lg font-medium text-gray-900 mt-1">{checkoutData.referenceCode}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Amount</p>
                          <p className="text-lg font-medium text-gray-900 mt-1">${checkoutData.amount}</p>
                        </div>
                      </div>
                      <div className="mt-4 p-4 bg-yellow-50 rounded-md">
                        <p className="text-base font-medium">⚠️ Payments without the correct Description may not be processed.</p>
                      </div>
                    </div>
                  )}
                </button>

                <h3 className="text-xl font-medium text-gray-900 mt-12 mb-4">Step 2: Check your email</h3>
                <div className="space-y-2">
                  <p className="text-base">Once your payment is received, you will receive your {productName} by email.</p>
                  <p className="text-base">⏳ This usually takes a few minutes, but may take up to an hour depending on your bank.</p>
                  <p className="text-base">📬 Please check your inbox and spam folder.</p>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {!checkoutData && (
        <div className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-base font-medium text-gray-700">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleInputChange}
              className="form-input mt-1 block w-full px-4 py-3 text-base"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-base font-medium text-gray-700">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              className="form-input mt-1 block w-full px-4 py-3 text-base"
            />
          </div>

          <div>
            <label htmlFor="quantity" className="block text-base font-medium text-gray-700">Quantity</label>
            <select
              id="quantity"
              name="quantity"
              required
              value={formData.quantity}
              onChange={handleInputChange}
              className="form-input mt-1 block w-full px-4 py-3 text-base"
            >
              {[1, 2, 3, 4, 5].map(num => (
                <option key={num} value={num}>{num} Pass{num > 1 ? 'es' : ''}</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-base ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Processing...' : 'Buy Now'}
          </button>
        </div>
      )}
    </form>
  );
}; 